import Button from '@app/components/ui/button';
import PdfEditorTextField from '@app/components/ui/input/pdf-editor-text-field';
import { useTranslation } from 'next-i18next';
import { useRef, useState } from 'react';
import { useModal } from '../context';
import { useUpdateDocumentMutation } from '@app/store/pdf/api';
import { toast } from 'react-toastify';
import { ToastId } from '@app/constants/ToastId';
import { useAppSelector } from '@app/store/hooks';
import { selectAuth } from '@app/store/auth/authSlice';

export default function RenameModal({ documentId, currentFileName, modifiedBy }: { documentId: string; currentFileName: string; modifiedBy: string }) {
    const { t } = useTranslation(['common', 'document']);
    const renameDocumentRef = useRef<HTMLInputElement | null>(null);
    const { closeModal } = useModal();
    const auth = useAppSelector(selectAuth);
    const [newFileName, setNewFileName] = useState(currentFileName.replace('.pdf', ''));
    const [updateDocument, { isLoading }] = useUpdateDocumentMutation();

    const handleCancel = (e: any) => {
        e.preventDefault();
        closeModal();
    };

    const handleSave = async () => {
        const trimmedFileName = newFileName.trim();

        if (!trimmedFileName || trimmedFileName === currentFileName.replace('.pdf', '')) {
            closeModal();
            return;
        }
        try {
            await updateDocument({
                documentId,
                uniqueFileName: `${newFileName}.pdf`,
                modifiedBy: auth.email ?? modifiedBy
            }).unwrap();

            closeModal();
            toast.success(`${t('toast.fileNameUpdated')}`, { toastId: ToastId.SUCCESS_TOAST });
        } catch (error) {
            console.error('Error updating file name:', error);
            toast.error(`${t('toast.fileNameUpdatedError')}`, { toastId: ToastId.ERROR_TOAST });
        }
    };

    return (
        <div className="flex h-full w-[30vw] flex-col rounded-xl bg-white p-3">
            <h1 className="pb-2 text-xl font-bold">{t('updateDisplayUserName', { ns: 'common' })}</h1>
            <div className="relative flex w-full items-center rounded-md border">
                <PdfEditorTextField
                    data-testid="rename-document"
                    id="rename-document"
                    inputRef={renameDocumentRef}
                    placeholder={t('enterFileName', { ns: 'document' })}
                    value={newFileName}
                    onChange={(e) => setNewFileName(e.target.value)}
                    className="!w-full !rounded-md pr-10"
                />
                <span className="absolute right-3 text-gray-500">.pdf</span>
            </div>
            <div className="ml-auto flex w-2/3 items-center justify-end gap-2 pt-6">
                <Button data-testid="cancel-button" onClick={handleCancel} className="flex-1 !border-gray-300 !bg-white !text-black-1 hover:!bg-gray-50" variant="outline" disabled={isLoading}>
                    {t('cancel')}
                </Button>
                <Button data-testid="save-button" className="flex-1" variant="solid" isLoading={isLoading} disabled={!newFileName.trim() || isLoading} onClick={handleSave}>
                    {t('save')}
                </Button>
            </div>
        </div>
    );
}
